import React, { useCallback, useEffect, useState } from "react";
import { CheckCircle, XCircle, TrashSimple, Snowflake } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "../../lib/api";
import CustomGiftPreview from "../CustomGiftPreview";

const FILTERS = ["under_review", "approved", "rejected", "removed", "all"];

/** AdminGifts — marketplace moderation queue, limited editions, analytics, fraud flags. */
export default function AdminGifts() {
  const [status, setStatus] = useState("under_review");
  const [data, setData] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [approveFor, setApproveFor] = useState(null);
  const [form, setForm] = useState({ price_waves: "", is_limited: false, limited_qty: "", expires_at: "", badge: "" });
  const [rejectFor, setRejectFor] = useState(null);
  const [reason, setReason] = useState("");
  const [limited, setLimited] = useState({ name: "", text: "", emoji: "🏆", animation: "shimmer", sfx: "fanfare", palette: "gold", category: "exclusive", price_waves: 500, limited_qty: "", expires_at: "", badge: "Limited" });
  const [showLimited, setShowLimited] = useState(false);

  const load = useCallback(async () => {
    try {
      const [{ data: d }, { data: a }] = await Promise.all([api.get("/admin/gifts", { params: { status } }), api.get("/admin/gifts/analytics")]);
      setData(d); setAnalytics(a);
    } catch (e) { toast.error("Could not load gifts"); }
  }, [status]);

  useEffect(() => { load(); }, [load]);

  const approve = async () => {
    try {
      await api.post(`/admin/gifts/${approveFor.id}/approve`, {
        price_waves: form.price_waves ? Number(form.price_waves) : null, is_limited: form.is_limited,
        limited_qty: form.limited_qty ? Number(form.limited_qty) : null, expires_at: form.expires_at || null, badge: form.badge || null,
      });
      toast.success("Gift approved — live in the shop");
      setApproveFor(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Approve failed"); }
  };
  const reject = async () => {
    try {
      await api.post(`/admin/gifts/${rejectFor.id}/reject`, { reason });
      toast.success("Gift rejected"); setRejectFor(null); setReason(""); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Reject failed"); }
  };
  const remove = async (g) => {
    if (!window.confirm(`Remove “${g.name}” from the marketplace?`)) return;
    try { await api.post(`/admin/gifts/${g.id}/remove`); toast.success("Removed"); load(); } catch (e) { toast.error("Remove failed"); }
  };
  const createLimited = async () => {
    try {
      await api.post("/admin/gifts/limited", { ...limited, price_waves: Number(limited.price_waves), limited_qty: limited.limited_qty ? Number(limited.limited_qty) : null, expires_at: limited.expires_at || null });
      toast.success("Limited-edition gift published"); setShowLimited(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const freeze = async (uid) => {
    try { const { data: r } = await api.post(`/admin/users/${uid}/freeze`); toast.success(r.wallet_frozen ? "Wallet frozen" : "Wallet unfrozen"); load(); } catch (e) { toast.error("Failed"); }
  };

  return (
    <div data-testid="admin-gifts">
      {analytics && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-5" data-testid="admin-gift-analytics">
          <Stat label="Approved" value={analytics.gifts.approved} sub={`${analytics.gifts.under_review} in review · ${analytics.gifts.rejected} rejected`} />
          <Stat label="Gift purchases" value={analytics.purchases.count} sub={`${analytics.purchases.total_waves.toLocaleString()} waves`} />
          <Stat label="Host rewards" value={`${analytics.purchases.host_rewards_pearls.toLocaleString()} 🪩`} sub={`${analytics.split.host_share_pct}% share`} />
          <Stat label="Platform share" value={`${analytics.purchases.platform_share_waves.toLocaleString()} 🪙`} sub={`creators ${analytics.purchases.creator_rewards_pearls} 🪩 (${analytics.split.creator_reward_pct}%)`} />
        </div>
      )}

      <div className="flex items-center justify-between mb-3">
        <div className="flex gap-1.5 overflow-x-auto">
          {FILTERS.map((f) => (
            <button key={f} onClick={() => setStatus(f)} data-testid={`admin-gifts-filter-${f}`} className={`flex-shrink-0 px-3 py-1.5 rounded-full text-[10px] font-black tracking-[0.15em] uppercase ${status === f ? "bg-papaya text-ink" : "bg-white/5 text-cream/60"}`}>
              {f.replace("_", " ")}{data?.counts?.[f] !== undefined ? ` · ${data.counts[f]}` : ""}
            </button>
          ))}
        </div>
        <button onClick={() => setShowLimited((v) => !v)} data-testid="admin-gifts-new-limited" className="flex-shrink-0 px-3 py-1.5 rounded-full bg-jam-gold text-jam-black text-[10px] font-black tracking-[0.15em] uppercase">+ Limited edition</button>
      </div>

      {showLimited && (
        <div className="mb-4 rounded-3xl bg-jam-gold/10 border border-jam-gold/30 p-4 space-y-2" data-testid="admin-limited-form">
          <p className="text-jam-gold text-[10px] tracking-[0.3em] uppercase font-black">Official limited-edition gift</p>
          <div className="grid grid-cols-2 gap-2">
            <input value={limited.name} onChange={(e) => setLimited({ ...limited, name: e.target.value })} placeholder="World Cup 2026" data-testid="admin-limited-name" className={inp} />
            <input value={limited.text} onChange={(e) => setLimited({ ...limited, text: e.target.value })} placeholder="Text on gift" className={inp} />
            <input value={limited.emoji} onChange={(e) => setLimited({ ...limited, emoji: e.target.value })} placeholder="Emoji" className={inp} />
            <input type="number" value={limited.price_waves} onChange={(e) => setLimited({ ...limited, price_waves: e.target.value })} placeholder="Price (waves)" data-testid="admin-limited-price" className={inp} />
            <input type="number" value={limited.limited_qty} onChange={(e) => setLimited({ ...limited, limited_qty: e.target.value })} placeholder="Qty available (blank = unlimited)" className={inp} />
            <input value={limited.expires_at} onChange={(e) => setLimited({ ...limited, expires_at: e.target.value })} placeholder="Expires (ISO, e.g. 2026-12-31T23:59:59+00:00)" className={inp} />
            <input value={limited.badge} onChange={(e) => setLimited({ ...limited, badge: e.target.value })} placeholder="Badge" className={inp} />
            <select value={limited.animation} onChange={(e) => setLimited({ ...limited, animation: e.target.value })} className={inp}>
              {["pop", "spin", "float", "burst", "shimmer", "rain", "heartbeat", "slide"].map((a) => <option key={a} value={a}>{a}</option>)}
            </select>
          </div>
          <button onClick={createLimited} data-testid="admin-limited-create" className="px-4 py-2 rounded-full bg-jam-gold text-jam-black text-[10px] font-black tracking-[0.2em] uppercase">Publish</button>
        </div>
      )}

      <div className="space-y-2">
        {(data?.items || []).map((g) => (
          <div key={g.id} className="flex items-start gap-3 p-3 rounded-3xl bg-white/5 border border-white/10" data-testid={`admin-gift-${g.id}`}>
            <CustomGiftPreview gift={g} size={80} className="flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-cream font-black truncate">{g.name} <span className="text-cream/40 text-[10px] font-normal">v{g.version} · {g.status}</span></p>
              <p className="text-cream/50 text-[11px]">by @{g.creator?.username || g.creator_username} · {g.category} · {g.price_waves} waves{g.is_limited ? " · LIMITED" : ""}</p>
              {g.description && <p className="text-cream/60 text-[11px] mt-0.5 line-clamp-2">{g.description}</p>}
              {g.moderation && <p className={`text-[10px] mt-0.5 ${g.moderation.text?.blocked || g.moderation.image?.blocked ? "text-red-300" : "text-emerald-300"}`}>Auto-moderation: {g.moderation.text?.blocked || g.moderation.image?.blocked ? "FLAGGED" : "clean"}{g.moderation.text?.error ? " (text check unavailable)" : ""}</p>}
              {g.status === "approved" && <p className="text-cream/50 text-[11px]">{g.sales_count} sales · {g.total_waves} waves · licensed: {g.ownership_status}</p>}
              <div className="flex gap-1.5 mt-2 flex-wrap">
                {["under_review", "submitted", "rejected", "removed"].includes(g.status) && (
                  <button onClick={() => { setApproveFor(g); setForm({ price_waves: g.price_waves, is_limited: !!g.is_limited, limited_qty: "", expires_at: "", badge: g.badge || "" }); }} data-testid={`admin-gift-approve-${g.id}`} className="px-3 py-1.5 rounded-full bg-emerald-400/20 text-emerald-300 text-[10px] font-black tracking-[0.15em] uppercase flex items-center gap-1"><CheckCircle size={12} weight="fill" /> Approve</button>
                )}
                {["under_review", "submitted", "approved"].includes(g.status) && (
                  <button onClick={() => setRejectFor(g)} data-testid={`admin-gift-reject-${g.id}`} className="px-3 py-1.5 rounded-full bg-red-500/20 text-red-200 text-[10px] font-black tracking-[0.15em] uppercase flex items-center gap-1"><XCircle size={12} weight="fill" /> Reject</button>
                )}
                {g.status === "approved" && (
                  <button onClick={() => remove(g)} data-testid={`admin-gift-remove-${g.id}`} className="px-3 py-1.5 rounded-full bg-white/10 text-cream/70 text-[10px] font-black tracking-[0.15em] uppercase flex items-center gap-1"><TrashSimple size={12} /> Remove</button>
                )}
              </div>
            </div>
          </div>
        ))}
        {data && data.items.length === 0 && <p className="text-cream/40 text-sm">Nothing in “{status.replace("_", " ")}”.</p>}
      </div>

      {approveFor && (
        <Modal title={`Approve “${approveFor.name}”`} onClose={() => setApproveFor(null)} testid="admin-approve-modal">
          <label className="block text-cream/60 text-[10px] tracking-[0.2em] uppercase font-bold">Marketplace price (waves)</label>
          <input type="number" value={form.price_waves} onChange={(e) => setForm({ ...form, price_waves: e.target.value })} data-testid="admin-approve-price" className={inp} />
          <label className="flex items-center gap-2 text-cream text-sm mt-3"><input type="checkbox" checked={form.is_limited} onChange={(e) => setForm({ ...form, is_limited: e.target.checked })} data-testid="admin-approve-limited" /> Limited edition</label>
          {form.is_limited && (
            <div className="grid grid-cols-2 gap-2 mt-2">
              <input type="number" value={form.limited_qty} onChange={(e) => setForm({ ...form, limited_qty: e.target.value })} placeholder="Qty" className={inp} />
              <input value={form.expires_at} onChange={(e) => setForm({ ...form, expires_at: e.target.value })} placeholder="Expires (ISO)" className={inp} />
            </div>
          )}
          <input value={form.badge} onChange={(e) => setForm({ ...form, badge: e.target.value })} placeholder="Badge (e.g. Creator Pick)" data-testid="admin-approve-badge" className={`${inp} mt-2`} />
          <button onClick={approve} data-testid="admin-approve-confirm" className="mt-4 w-full py-3 rounded-full bg-emerald-400 text-ink text-[10px] font-black tracking-[0.2em] uppercase">Approve & publish</button>
        </Modal>
      )}
      {rejectFor && (
        <Modal title={`Reject “${rejectFor.name}”`} onClose={() => setRejectFor(null)} testid="admin-reject-modal">
          <textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason shown to the creator (copyright, trademark, hate, sexual, violence, impersonation…)" data-testid="admin-reject-reason" className={inp} />
          <button onClick={reject} disabled={reason.trim().length < 3} data-testid="admin-reject-confirm" className="mt-4 w-full py-3 rounded-full bg-red-500 text-cream text-[10px] font-black tracking-[0.2em] uppercase disabled:opacity-40">Reject</button>
        </Modal>
      )}

      {analytics && (
        <div className="mt-8 grid sm:grid-cols-2 gap-3">
          <div className="rounded-3xl bg-white/5 border border-white/10 p-4">
            <p className="text-cream/50 text-[10px] tracking-[0.25em] uppercase font-bold mb-2">Most popular gifts</p>
            {analytics.top_gifts.length === 0 ? <p className="text-cream/40 text-xs">No sales yet.</p> : analytics.top_gifts.map((g) => (
              <div key={g.gift_id} className="flex justify-between text-xs text-cream/80 py-1 border-b border-white/5"><span>{g.gift_id}</span><span>{g.count} × · {g.waves} 🪙</span></div>
            ))}
          </div>
          <div className="rounded-3xl bg-white/5 border border-white/10 p-4">
            <p className="text-cream/50 text-[10px] tracking-[0.25em] uppercase font-bold mb-2">Most gifted hosts · top designers</p>
            {analytics.top_hosts.map((h) => <div key={h.user.id} className="flex justify-between text-xs text-cream/80 py-1 border-b border-white/5"><span>🎥 @{h.user.username || h.user.id.slice(0, 6)}</span><span>{h.waves} 🪙</span></div>)}
            {analytics.top_creators.map((c) => <div key={c.user.id} className="flex justify-between text-xs text-cream/80 py-1 border-b border-white/5"><span>🎨 @{c.user.username || c.user.id.slice(0, 6)}</span><span>{c.gifts} gifts · {c.sales} sales</span></div>)}
          </div>
          <div className="rounded-3xl bg-red-500/5 border border-red-500/20 p-4 sm:col-span-2" data-testid="admin-fraud">
            <p className="text-red-300 text-[10px] tracking-[0.25em] uppercase font-bold mb-2">Anti-fraud · {analytics.fraud.frozen_accounts} frozen wallets</p>
            {analytics.fraud.flags.length === 0 ? <p className="text-cream/40 text-xs">No suspicious activity flagged.</p> : analytics.fraud.flags.map((f) => (
              <div key={f.id} className="flex items-center justify-between text-xs text-cream/80 py-1.5 border-b border-white/5" data-testid={`admin-fraud-flag-${f.id}`}>
                <span>@{f.username} — {f.reason} <span className="text-cream/40">({f.kind})</span></span>
                <button onClick={() => freeze(f.user_id)} data-testid={`admin-freeze-${f.user_id}`} className="px-2.5 py-1 rounded-full bg-red-500/20 text-red-200 text-[9px] font-black tracking-[0.15em] uppercase flex items-center gap-1"><Snowflake size={10} weight="fill" /> Freeze / unfreeze</button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

const inp = "w-full bg-white/5 border border-white/10 rounded-2xl px-3 py-2.5 text-cream placeholder:text-cream/30 text-sm focus:outline-none focus:border-papaya";

function Stat({ label, value, sub }) {
  return (
    <div className="rounded-3xl border bg-white/5 border-white/10 p-3">
      <p className="text-cream/50 text-[10px] tracking-[0.25em] uppercase font-bold">{label}</p>
      <p className="text-cream text-2xl font-black tracking-tighter mt-0.5">{value}</p>
      {sub && <p className="text-cream/40 text-[10px] mt-0.5">{sub}</p>}
    </div>
  );
}

function Modal({ title, onClose, children, testid }) {
  return (
    <div className="fixed inset-0 z-[130] flex items-center justify-center p-5" data-testid={testid}>
      <div className="absolute inset-0 bg-ink/80 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-md bg-ink rounded-3xl border border-white/10 p-5">
        <h3 className="text-cream text-lg font-black tracking-tight mb-3">{title}</h3>
        {children}
      </div>
    </div>
  );
}
